import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import type { ExternalUser } from '@/types/externalUser';
import { toast } from 'sonner';
import { Loader2, Eye, Pencil } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';

export function ExternalUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<ExternalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await api.externalUsers.list();
        setUsers(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [reloadKey]);

  // Manual refresh shows the loading state again and clears the previous error.
  const fetchUsers = () => {
    setLoading(true);
    setError(null);
    setReloadKey(k => k + 1);
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.external_user_id.toLowerCase().includes(query) ||
      user.display_name?.toLowerCase().includes(query)
    );
  });

  const handleViewClick = (userId: number) => {
    navigate(`/external-users/${userId}`);
  };

  const handleEditClick = (user: ExternalUser) => {
    setEditingUser(user.id);
    setEditValue(user.display_name || '');
  };

  const handleEditCancel = () => {
    setEditingUser(null);
    setEditValue('');
  };

  const handleEditSave = async (userId: number) => {
    setSaving(true);
    try {
      const updated = await api.externalUsers.update(userId, { display_name: editValue });
      setUsers(prev => prev.map(u => u.id === userId ? updated : u));
      setEditingUser(null);
      setEditValue('');
      toast.success('Display name updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="External Users" description="Tenants identified by external_user_id." />
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Loading users...
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="External Users" description="Tenants identified by external_user_id." />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Users</CardTitle>
              <CardDescription>
                {users.length} external users registered
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search by ID or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64"
              />
              <Button size="sm" variant="outline" onClick={fetchUsers}>
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {filteredUsers.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No external users yet. Users are created automatically when apps are deployed.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>External User ID</TableHead>
                  <TableHead>Display Name</TableHead>
                  <TableHead>Apps</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.id}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {user.external_user_id}
                    </TableCell>
                    <TableCell>
                      {editingUser === user.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-48"
                            autoFocus
                          />
                          <Button
                            size="sm"
                            disabled={saving}
                            onClick={() => handleEditSave(user.id)}
                          >
                            {saving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleEditCancel}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <span>{user.display_name || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{user.apps_count}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatRelativeTime(user.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleViewClick(user.id)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {editingUser !== user.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditClick(user)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}